import React from 'react';

import { FormControl as MuiFormControl } from '@material-ui/core';

import { useId } from '@coursera/cds-common';
import type { OverrideProps } from '@coursera/cds-common';

import { FormControlContext } from './FormControlContext';
import getFormControlCss, { classes } from './getFormControlCss';
import type { ValidationStatus } from './types';

export type BaseFormControlProps = {
  /**
   * Unique `id` attribute for the input element.
   */
  id?: string;

  /**
   * Defines addition classes that will be added to the root element
   */
  className?: string;

  /**
   * Defines if field is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Defines if field should take full width of its container
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Defines support text for the field.
   * Field refers to it with `aria-describedby`
   *
   * Note: When using a `renderLabel` that has a better/longer accessible description,
   * we recommended skipping this prop to avoid overwhelming screenreader users with information.
   */
  supportText?: React.ReactNode;

  /**
   * Defines field as optional.
   * All fields are mandatory by default
   * @default false
   */
  optional?: boolean;

  /**
   * Defines field success/error styling.
   */
  validationStatus?: ValidationStatus;

  /**
   * Invert the color scheme. Use when displaying over dark backgrounds
   * @default false
   */
  invert?: boolean;
};

export interface FormControlTypeMap<D extends React.ElementType = 'div'> {
  props: BaseFormControlProps;
  defaultComponent: D;
}

export type Props<
  D extends React.ElementType = FormControlTypeMap['defaultComponent']
> = OverrideProps<FormControlTypeMap<D>, D> & { component?: React.ElementType };

export type FormControlProps = {
  children?: React.ReactNode;
} & Props;

const FormControl = (
  props: FormControlProps,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement<FormControlProps> => {
  const {
    id: idFromProps,
    children,
    invert,
    disabled,
    fullWidth,
    optional,
    supportText,
    validationStatus,
    ...rest
  } = props;

  const id = useId(idFromProps);

  const isValidated = validationStatus !== undefined;
  const supportTextId = supportText ? `${id}-support-text` : undefined;
  const inputLabelId = `${id}-label`;
  const validationLabelId = isValidated ? `${id}-validation-label` : undefined;

  const formControlContext = {
    id,
    validationStatus,
    invert,
    labelId: inputLabelId,
    validationLabelId: validationLabelId,
    supportTextId: supportTextId,
    ariaDescribedBy: isValidated
      ? [validationLabelId, supportTextId].filter(Boolean).join(' ')
      : supportTextId,
  };

  return (
    <FormControlContext.Provider value={formControlContext}>
      <MuiFormControl
        ref={ref}
        classes={{ root: classes.root }}
        css={getFormControlCss}
        disabled={disabled}
        error={validationStatus === 'error'}
        fullWidth={fullWidth}
        required={!optional}
        variant="standard"
        {...rest}
      >
        {children}
      </MuiFormControl>
    </FormControlContext.Provider>
  );
};

/**
 * FormControl
 * Wraps input components (Input, Select, etc).
 */
export default React.forwardRef(FormControl);
